/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Settings Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.25.0
 * @since 1.3.0
 */

import { FC, memo } from 'react';

import { connect } from 'react-redux';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { SideBar } from '../../components/common';
import Title from '../../components/common/Title/Title';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../modules/internationalization/constants';
import { makeSelectTranslationPageSettings } from '../../modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '../../modules/internationalization/types/common/pages';
import { IPageSettingsTranslation } from '../../modules/internationalization/types/pages/pageSettings';
import { Loader } from '../../modules/loader/components/Loader';
import { AccessComponentKeyEnum } from '../../modules/role/constants/guard/guardConstants';
import { IRootReducer } from '../../reducers/rootReducer';
import { RoutePath } from '../../routes/constants/routesConstants';
import PrivateRoute from '../../routes/PrivateRoute';
import PageNotFound from '../ErrorPage/PageNotFound';
import PageSettingsSideMenu from './components/PageSettingsSideMenu';
import PageSettingsTitle from './components/PageSettingsTitle';
import SettingsAutoReports from './components/SettingsAutoReports/components/SettingsAutoReports';
import SettingsLabels from './components/SettingsLabels/components/SettingsLabels';
import SettingsNotifications from './components/SettingsNotifications/components/SettingsNotifications';
import SettingsPositions from './components/SettingsPositions/components/SettingsPositions';
import SettingsProfile from './components/SettingsProfile/components/SettingsProfile';
import SettingsStatistics from './components/SettingsStatistics/components/SettingsStatistics';
import { PageSettingsWrapperStyled } from './components/styled/PageSettingsStyled';
import { makeSelectSettingsIsShowLoader } from './settingsSelectors';

export type TPageSettings = IConnectedProps & RouteComponentProps<IMatchProps>;

export interface IMatchProps {
    section: string;
}

const PageSettings: FC<TPageSettings> = (props: TPageSettings) => {
    const activeItemValue = props.match.params.section;

    return (
        <PageLocale
            data-page="settings"
            fileNames={[
                TranslationFileNamesEnum.common,
                TranslationFileNamesEnum.corporate,
                TranslationFileNamesEnum.pageSettings,
            ]}
        >
            <Title pageName={ECommonTranslationPages.settings} />

            <PageSettingsTitle />

            <PageSettingsWrapperStyled>
                <Loader showLoader={props.isShowLoader} />
                <SideBar navWidth={240}>
                    <PageSettingsSideMenu
                        activeItemValue={activeItemValue}
                        settingsTranslation={props.settingsTranslation}
                    />

                    <div>
                        <Switch>
                            <Route exact component={SettingsProfile} path={RoutePath.settingsProfile} />
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PageSettingsAutoReports}
                                component={SettingsAutoReports}
                                path={RoutePath.settingsAutoReports}
                            />
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PageSettingsStatistics}
                                component={SettingsStatistics}
                                path={RoutePath.settingsStatistics}
                            />
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PageSettingsLabels}
                                component={SettingsLabels}
                                path={RoutePath.settingsLabels}
                            />
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.PageSettingsPositions}
                                component={SettingsPositions}
                                path={RoutePath.settingsPositions}
                            />
                            <PrivateRoute
                                exact
                                accessKey={AccessComponentKeyEnum.notifications}
                                component={SettingsNotifications}
                                path={RoutePath.settingsNotifications}
                            />

                            <Route component={PageNotFound} path="**" />
                        </Switch>
                    </div>
                </SideBar>
            </PageSettingsWrapperStyled>
        </PageLocale>
    );
};

export interface IConnectedProps {
    isShowLoader: boolean;
    settingsTranslation: IPageSettingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isShowLoader: makeSelectSettingsIsShowLoader(),
    settingsTranslation: makeSelectTranslationPageSettings(),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(memo(PageSettings));
